import { apiFetch, type ApiResponse } from "./api";
import type { OkResponse, ProfileResponse } from "./types";

// Get current user profile
export async function getCurrentUser(): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>("/api/users/me");
}

/** Готовность анкеты к подбору (issue #946): какие обязательные поля отсутствуют. */
export interface ProfileReadiness {
  profileCompleted: boolean;
  ready: boolean;
  missing: string[];
}

export async function getProfileReadiness(): Promise<ApiResponse<ProfileReadiness>> {
  return apiFetch<ProfileReadiness>("/api/users/me/profile-readiness");
}

// Update current user profile
export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  birthDate?: string;
  birthTime?: string;
  birthCity?: string;
  gender?: "male" | "female";
  lifestyle?: { alcohol?: string; smoking?: string; children?: string; work?: string; education?: string };
  relationshipGoal?: string;
  bio?: string;
  location?: string;
  avatarUrl?: string;
  photos?: string[];
  interests?: string[];
  personality?: any;
  astrology?: any;
  numerology?: any;
}

/**
 * Код подтверждения email из профиля (#1285). Идентификатора челленджа нет: подтверждение идёт по сессии.
 * `sent: false` — код не выпущен (`rateLimited` — упёрлись в частоту, иначе почта недоступна).
 */
export interface EmailVerificationInfo {
  sent: boolean;
  rateLimited?: boolean;
  emailHint?: string;
  expiresInSeconds?: number;
  resendAfterSeconds?: number;
  emailDelivered?: boolean;
  /** Только DEV-стенд. */
  devCode?: string | null;
}

export type UpdateProfileResponse = ProfileResponse & { emailVerification?: EmailVerificationInfo };

/** Прислать (повторно) код подтверждения на ожидающий адрес профиля. */
export async function sendProfileEmailCode(): Promise<ApiResponse<EmailVerificationInfo>> {
  return apiFetch<EmailVerificationInfo>("/api/users/me/email/verification/send", {
    method: "POST",
  });
}

/** Отменить ожидающую смену адреса. */
export async function cancelPendingEmail(): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>("/api/users/me/email/pending", { method: "DELETE" });
}

/** Подтвердить адрес из профиля кодом из письма. */
export async function confirmProfileEmailCode(code: string): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>("/api/users/me/email/verification/confirm", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

/** Привязать VK ID к текущему аккаунту (#1296): сервер сам спрашивает VK, чей это токен; `vkId` лишь сверяется. */
export async function linkVkAccount(data: { vkId: string; accessToken: string }): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>("/api/users/me/link/vk", { method: "POST", body: JSON.stringify(data) });
}

/** Привязать Яндекс ID к текущему аккаунту (#1296): код обменивает сервер. */
export async function linkYandexAccount(data: { code: string; redirectUri: string }): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>("/api/users/me/link/yandex", { method: "POST", body: JSON.stringify(data) });
}

export async function updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<UpdateProfileResponse>> {
  return apiFetch<UpdateProfileResponse>("/api/users/me", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/**
 * Финал визарда регистрации для входа через VK ID / Яндекс ID (#1279): анкета и
 * согласия без email/пароля. `avatarUrl` не передаём, если оставлено фото
 * провайдера — оно уже лежит в профиле. Повтор у заполненной анкеты безопасен.
 */
export interface CompleteRegistrationRequest {
  name: string;
  birthDate: string;
  birthTime?: string;
  birthCity?: string;
  gender: "male" | "female";
  avatarUrl?: string;
  photos?: string[];
  bio?: string;
  interests?: string[];
  location?: string;
  relationshipGoal?: string;
  agreeToPrivacy: boolean;
  agreeToTerms: boolean;
  agreeToOffer: boolean;
  agreeToAge18: boolean;
}

export async function completeRegistration(
  data: CompleteRegistrationRequest,
): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>("/api/users/me/complete-registration", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Get user profile by ID
export async function getUserById(id: string): Promise<ApiResponse<ProfileResponse>> {
  return apiFetch<ProfileResponse>(`/api/users/${encodeURIComponent(id)}`);
}

/**
 * Токен профиля для QR-кода текущего пользователя.
 *
 * Зачем ходить на бэкенд, если QR тривиально собирается на клиенте: в
 * Capacitor-обёртке (androidScheme: 'https') `window.location.origin` равен
 * `https://localhost` и попадал в QR — сканирующий улетал на несуществующий
 * localhost. Бэкенд знает публичный домен (`settings.qr_site_base_url`) и
 * возвращает готовую ссылку — она одинаково корректна и на вебе, и в APK, без
 * ветвления по `Capacitor.isNativePlatform()`. Эндпоинт добавлен в PR #1040.
 *
 * `token` — идентификатор пользователя (совпадает с сегодняшним `user.id` в
 * QR-URL сайта); `url` — ровно то, что кодировать в QR. `allowQrScan` дублирует
 * настройку приватности из `/me/settings` — модалка может показать «QR отключён»
 * до рендера. `expiresAt` пока всегда `null` (см. `backend/app/api/qr_routes.py`).
 */
export interface QrTokenResponse {
  token: string;
  url: string;
  userId: string;
  expiresAt: string | null;
  allowQrScan: boolean;
}

export async function getMyQrToken(): Promise<ApiResponse<QrTokenResponse>> {
  return apiFetch<QrTokenResponse>("/api/qr/token");
}

// Удаление собственного аккаунта (мягкая инвалидация всех токенов на бэке).
export async function deleteAccount(): Promise<ApiResponse<OkResponse>> {
  return apiFetch<OkResponse>("/api/users/me", {
    method: "DELETE",
  });
}

// «Ещё» на карточке анкеты → «Скрыть профиль для этого пользователя»: односторонняя
// видимость (не блокировка переписки/лайков) — профиль больше не попадается в ленте.
export async function hideUserProfile(id: string): Promise<ApiResponse<OkResponse>> {
  return apiFetch<OkResponse>(`/api/users/${encodeURIComponent(id)}/hide`, {
    method: "POST",
  });
}
